'use client';

import { AlertCircle, CheckCircle2, Loader2, MinusCircle, RefreshCw } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { trpc } from '@/lib/api/trpc';
import { useBulkSyncStoreIntegrations } from '@/lib/api/products';
import type { Store } from '@/lib/api/stores';
import {
  buildSyncEligibility,
  getProviderLabel,
  type BulkSyncIntegrationsResult,
  type SyncEligibility,
} from '@/app/stores/utils/bulk-sync-integrations';

type BulkSyncIntegrationsModalProps = {
  stores: Store[];
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

type ModalStep = 'confirm' | 'result';

const getFailureReason = (reason: string, t: (key: string) => string) => {
  if (reason === 'rate_limit') return t('table.syncDialog.failureRateLimit');
  if (reason === 'inngest_unavailable') return t('table.syncDialog.failureInngest');
  if (reason === 'unknown_error') return t('table.syncDialog.failureUnknown');
  return reason;
};

const notifyBulkSyncResult = (
  result: BulkSyncIntegrationsResult,
  ignoredCount: number,
  t: (key: string, options?: Record<string, unknown>) => string,
  toast: ReturnType<typeof useToast>['toast'],
) => {
  if (result.failed.length > 0 && result.started === 0) {
    toast({
      title: t('table.syncDialog.allFailedTitle'),
      description: t('table.syncDialog.allFailedDescription', { count: result.failed.length }),
      variant: 'destructive',
    });
    return;
  }
  if (result.failed.length > 0) {
    toast({
      title: t('table.syncDialog.partialFailureTitle'),
      description: t('table.syncDialog.partialFailureDescription', {
        started: result.started,
        ignored: ignoredCount,
        failed: result.failed.length,
      }),
      variant: 'destructive',
    });
    return;
  }
  toast({
    title: t('table.syncDialog.successTitle'),
    description: t('table.syncDialog.success', { started: result.started, ignored: ignoredCount }),
  });
};

type BulkSyncResultPanelProps = {
  result: BulkSyncIntegrationsResult;
  ignoredCount: number;
  t: (key: string, options?: Record<string, unknown>) => string;
};

const BulkSyncResultPanel = ({ result, ignoredCount, t }: BulkSyncResultPanelProps) => (
  <div className="space-y-3 px-6 py-4">
    {result.started > 0 ? (
      <div className="flex items-start gap-2 text-sm text-green-600">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{t('table.syncDialog.resultStarted', { count: result.started })}</span>
      </div>
    ) : null}
    {ignoredCount > 0 ? (
      <div className="text-muted-foreground flex items-start gap-2 text-sm">
        <MinusCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{t('table.syncDialog.resultIgnored', { count: ignoredCount })}</span>
      </div>
    ) : null}
    {result.failed.length > 0 ? (
      <div className="space-y-2">
        <div className="text-destructive flex items-start gap-2 text-sm font-medium">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{t('table.syncDialog.resultFailed', { count: result.failed.length })}</span>
        </div>
        <ul className="bg-muted/50 max-h-36 space-y-1.5 overflow-y-auto rounded-md border p-3 text-xs">
          {result.failed.map((item) => (
            <li key={item.storeId}>
              <span className="font-medium">{item.storeName}</span>
              <span className="text-muted-foreground"> — {getFailureReason(item.reason, t)}</span>
            </li>
          ))}
        </ul>
      </div>
    ) : null}
  </div>
);

type EligibilityPreviewProps = {
  eligibility: SyncEligibility;
  isLoading: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
};

const EligibilityPreview = ({ eligibility, isLoading, t }: EligibilityPreviewProps) => {
  if (isLoading) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 px-6 py-4 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('table.syncDialog.loadingEligibility')}
      </div>
    );
  }
  return (
    <div className="space-y-3 px-6 py-4 text-sm">
      {eligibility.eligible.length > 0 ? (
        <div>
          <p className="mb-1.5 font-medium">{t('table.syncDialog.willSyncTitle')}</p>
          <ul className="bg-muted/50 space-y-1 rounded-md border p-3 text-xs">
            {eligibility.eligible.map((store) => (
              <li key={store.id}>
                <span className="font-medium">{store.name}</span>
                <span className="text-muted-foreground"> — {getProviderLabel(store.provider)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-muted-foreground">{t('table.syncDialog.noEligibleStores')}</p>
          <ul className="text-muted-foreground list-inside list-disc text-xs">
            {eligibility.ignored.map((store) => (
              <li key={store.id}>{store.name}</li>
            ))}
          </ul>
        </div>
      )}
      {eligibility.eligible.length > 0 && eligibility.ignored.length > 0 ? (
        <div>
          <p className="text-muted-foreground mb-1.5 font-medium">{t('table.syncDialog.ignoredTitle')}</p>
          <ul className="text-muted-foreground space-y-1 text-xs">
            {eligibility.ignored.map((store) => (
              <li key={store.id}>
                {store.name} — {t('table.syncDialog.ignoredReason')}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {eligibility.eligible.length > 0 ? (
        <p className="text-muted-foreground text-xs">{t('table.syncDialog.confirmHint')}</p>
      ) : null}
    </div>
  );
};

export const BulkSyncIntegrationsModal = ({
  stores,
  open,
  onClose,
  onSuccess,
}: BulkSyncIntegrationsModalProps) => {
  const { t } = useTranslation('stores');
  const { t: tCommon } = useTranslation('common');
  const { toast } = useToast();
  const bulkSyncMutation = useBulkSyncStoreIntegrations();
  const [step, setStep] = React.useState<ModalStep>('confirm');
  const [result, setResult] = React.useState<BulkSyncIntegrationsResult | null>(null);
  const [ignoredCount, setIgnoredCount] = React.useState(0);

  const storeRows = React.useMemo(
    () => stores.map((store) => ({ id: store.id, name: store.name })),
    [stores],
  );

  const integrationQueries = trpc.useQueries((tRpc) =>
    storeRows.map((store) =>
      tRpc.integrations.list({ storeId: store.id }, { enabled: open && !!store.id }),
    ),
  );

  const isLoadingEligibility = integrationQueries.some((query) => query.isLoading);

  const eligibility = React.useMemo(() => {
    const integrationsByStoreId = Object.fromEntries(
      storeRows.map((store, index) => [store.id, integrationQueries[index]?.data]),
    );
    return buildSyncEligibility(storeRows, integrationsByStoreId);
  }, [storeRows, integrationQueries]);

  React.useEffect(() => {
    if (!open) return;
    setStep('confirm');
    setResult(null);
    setIgnoredCount(0);
  }, [open]);

  const handleStartSync = async () => {
    if (eligibility.eligible.length === 0) return;
    setIgnoredCount(eligibility.ignored.length);
    try {
      const bulkResult = await bulkSyncMutation.mutateAsync({ stores: eligibility.eligible });
      setResult(bulkResult);
      setStep('result');
      notifyBulkSyncResult(bulkResult, eligibility.ignored.length, t, toast);
      if (bulkResult.started > 0) onSuccess?.();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('table.syncDialog.error');
      toast({ title: t('table.syncDialog.error'), description: message, variant: 'destructive' });
    }
  };

  const handleClose = () => {
    setStep('confirm');
    setResult(null);
    setIgnoredCount(0);
    onClose();
  };

  const isPending = bulkSyncMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && !isPending && handleClose()}>
      <DialogContent className="max-w-md gap-0 overflow-hidden p-0">
        <DialogHeader className="space-y-1 border-b px-6 py-5 text-left">
          <div className="flex items-center gap-2">
            <div className="bg-primary/10 text-primary flex h-8 w-8 items-center justify-center rounded-md">
              <RefreshCw className="h-4 w-4" />
            </div>
            <DialogTitle>
              {step === 'result' ? t('table.syncDialog.resultTitle') : t('table.syncDialog.title')}
            </DialogTitle>
          </div>
          <DialogDescription>
            {step === 'result'
              ? t('table.syncDialog.resultDescription')
              : t('table.syncDialog.description', { count: stores.length })}
          </DialogDescription>
        </DialogHeader>

        {step === 'confirm' ? (
          <EligibilityPreview eligibility={eligibility} isLoading={isLoadingEligibility} t={t} />
        ) : (
          result && <BulkSyncResultPanel result={result} ignoredCount={ignoredCount} t={t} />
        )}

        <DialogFooter className="border-t px-6 py-4">
          {step === 'confirm' ? (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isPending}>
                {tCommon('cancel')}
              </Button>
              <Button
                onClick={() => void handleStartSync()}
                disabled={isPending || isLoadingEligibility || eligibility.eligible.length === 0}
              >
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {isPending
                  ? t('table.syncDialog.starting', { count: eligibility.eligible.length })
                  : t('table.syncDialog.confirm')}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose}>{t('table.syncDialog.done')}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
